import React, { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Grid, Chip, CircularProgress, alpha, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchRounded';
import GroupsIcon from '@mui/icons-material/GroupsRounded';
import { motion } from 'framer-motion';
import { getStudentAssignmentsByCourseBatch } from '../api/teacherApi';
import EmptyState from '../components/EmptyState';
import { staggerContainer, fadeInUp } from '../utils/motion';

const MotionPaper = motion(Paper);

// No "list my students" endpoint was shared, so this is search-by-admission-ID
// for now — same pattern as Certificates.jsx on the student side. Swap for a
// full roster view once a list endpoint exists.
const Students = () => {
  const theme = useTheme();
  const [admissionId, setAdmissionId] = useState('');
  const [course, setCourse] = useState('');
  const [batch, setBatch] = useState('');
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!admissionId) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await getStudentAssignmentsByCourseBatch(admissionId, course, batch);
      const data = res.data;
      setGroups(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error(err);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Student records</Typography>

      <MotionPaper
        variants={fadeInUp} initial="hidden" animate="show"
        elevation={0}
        sx={{
          display: 'flex', gap: 2, mb: 2.5, alignItems: 'center', flexWrap: 'wrap',
          p: 2, borderRadius: '16px', border: '1px solid', borderColor: 'divider',
        }}
      >
        <TextField
          label="Admission ID" size="small" value={admissionId}
          onChange={(e) => setAdmissionId(e.target.value)} sx={{ width: 160 }}
        />
        <TextField
          label="Course ID (optional)" size="small" value={course}
          onChange={(e) => setCourse(e.target.value)} sx={{ width: 160 }}
        />
        <TextField
          label="Batch ID (optional)" size="small" value={batch}
          onChange={(e) => setBatch(e.target.value)} sx={{ width: 160 }}
        />
        <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={!admissionId}>
          Search
        </Button>
      </MotionPaper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !searched ? (
        <EmptyState
          icon={<GroupsIcon />}
          title="Search for a student"
          subtitle="Enter an admission ID above to see their assignments, grouped by course and batch."
        />
      ) : groups && groups.length > 0 ? (
        <Grid container spacing={2} component={motion.div} variants={staggerContainer} initial="hidden" animate="show">
          {groups.map((g, i) => (
            <Grid item xs={12} sm={6} md={4} key={g.id || i}>
              <MotionPaper
                variants={fadeInUp}
                elevation={0}
                sx={{ p: 2.5, borderRadius: '18px', border: '1px solid', borderColor: 'divider', height: '100%' }}
              >
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>{g.title || g.assignment_title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {g.course_name || `Course ${g.course}`}{(g.batch_name || g.batch) && ` — ${g.batch_name || `Batch ${g.batch}`}`}
                </Typography>
                {g.status && (
                  <Chip
                    size="small" label={g.status}
                    sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, textTransform: 'capitalize' }}
                  />
                )}
              </MotionPaper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyState
          icon={<GroupsIcon />}
          title="No records found"
          subtitle="Check the admission ID and try again."
        />
      )}
    </Box>
  );
};

export default Students;
